import React, { useEffect, useMemo, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Spin } from "antd";

import { Select } from "../../../shared";
import { useDebouncedValue } from "../../../../hooks/useDebouncedSearchQuery";
import {
  type CasinoDeeplinkDropdownItem,
  type CasinoDeeplinkSearchType,
  useCasinoDeeplinkDropdown,
} from "../../../../queries/dynamicBonuses/useCasinoDeeplinkDropdown";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import {
  getRestrictionDisplayName,
  useRestrictionDisplayName,
} from "./restrictionDisplayNames";
import styles from "../stylesDynamicBonuses.module.css";

const SEARCH_DEBOUNCE_MS = 300;

type Props = {
  searchType: CasinoDeeplinkSearchType;
  label: string;
  placeholder?: string;
  value?: string;
  /** Human-readable label for `value` when the option is not in the current search results. */
  displayName?: string;
  onSelect: (item: CasinoDeeplinkDropdownItem) => void;
  onClear: () => void;
  disabled?: boolean;
};

const CasinoDeeplinkSearchField: React.FC<Props> = ({
  searchType,
  label,
  placeholder,
  value,
  displayName,
  onSelect,
  onClear,
  disabled = false,
}) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const nameCache = useRef<Map<string, string>>(new Map());
  const cachedRestrictionName = useRestrictionDisplayName(value);

  const resolvedDisplayName = displayName ?? cachedRestrictionName;

  const lookupSearch =
    value && !resolvedDisplayName && !nameCache.current.has(value) ? value : "";
  const dropdownSearch = debouncedSearch.trim() ? debouncedSearch : lookupSearch;

  const { data: options = [], isLoading } = useCasinoDeeplinkDropdown(
    searchType,
    dropdownSearch,
    !disabled,
  );

  useEffect(() => {
    for (const opt of options) {
      nameCache.current.set(opt.id, opt.name);
    }
  }, [options]);

  useEffect(() => {
    if (!value) return;
    const cachedName = getRestrictionDisplayName(value);
    if (cachedName) {
      nameCache.current.set(value, cachedName);
    }
  }, [value, resolvedDisplayName]);

  const selectOptions = useMemo(() => {
    const mapped = options.map((opt) => ({
      label: `${opt.name} (${opt.id})`,
      value: opt.id,
    }));

    if (value && !mapped.some((opt) => opt.value === value)) {
      const name =
        nameCache.current.get(value) ?? resolvedDisplayName ?? value;
      mapped.unshift({ label: `${name} (${value})`, value });
    }

    return mapped;
  }, [options, value, resolvedDisplayName]);

  const handleChange = (nextValue: string | undefined) => {
    if (!nextValue) {
      onClear();
      setSearch("");
      return;
    }

    const item = options.find((opt) => opt.id === nextValue);
    if (!item) return;
    onSelect(item);
    setSearch("");
  };

  return (
    <div
      className={styles.casinoDeeplinkSearchField}
      data-testid={`casino-deeplink-${searchType}`}
    >
      <div className={styles.casinoDeeplinkSearchHeader}>
        <label className={styles.casinoDeeplinkSearchLabel}>{label}</label>
      </div>

      <Select
        showSearch
        allowClear
        disabled={disabled}
        placeholder={
          placeholder ??
          formatString(AppStrings.PAM_DYNAMIC_BONUS_CASINO_DEEPLINK_SEARCH_PLACEHOLDER, {
            label: label.toLowerCase(),
          })
        }
        value={value ?? undefined}
        searchValue={search}
        onSearch={setSearch}
        onChange={(nextValue) => handleChange(nextValue as string | undefined)}
        options={selectOptions}
        filterOption={false}
        notFoundContent={
          isLoading
            ? <Spin size="small" />
            : AppStrings.NO_RESULTS
        }
        suffixIcon={<SearchOutlined />}
        className={styles.casinoDeeplinkSearchSelect}
      />
    </div>
  );
};

export default CasinoDeeplinkSearchField;
